import { useState } from "react";
import { addAttachmentsToTicket } from "../../../api/ticketApi.js";
import toast from "react-hot-toast";
import "./AddAttachments.css";

const AddAttachments = ({ ticketId, onAttachmentsAdded, onClose }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);

    /**
     * Handle file selection
     */
    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        
        // Validate file count (max 5 files)
        if (selectedFiles.length > 5) {
            toast.error("You can upload maximum 5 files.");
            return;
        }

        // Validate file sizes (max 50MB each)
        const maxSize = 50 * 1024 * 1024; // 50MB
        for (let file of selectedFiles) {
            if (file.size > maxSize) {
                toast.error(`File ${file.name} is too large. Maximum file size is 50MB.`);
                return;
            }
        }

        setFiles(selectedFiles);
        toast.success(`${selectedFiles.length} file(s) selected`);
    };

    /**
     * Remove a selected file
     */
    const removeFile = (index) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
        toast.success("File removed");
    };

    /**
     * Get file icon based on extension
     */
    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'las la-image';
        if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(ext)) return 'las la-video';
        if (ext === 'pdf') return 'las la-file-pdf';
        return 'las la-file';
    };

    /**
     * Handle submit
     */
    const handleSubmit = async () => {
        if (files.length === 0) {
            toast.error("Please select at least one file");
            return;
        }

        try {
            setUploading(true);

            // Create FormData
            const formData = new FormData();
            files.forEach((file) => {
                formData.append('attachments', file);
            });

            const response = await addAttachmentsToTicket(ticketId, formData);

            if (response.success) {
                toast.success(`Successfully added ${files.length} attachment(s)`);
                setFiles([]);
                if (onAttachmentsAdded) {
                    onAttachmentsAdded();
                }
                if (onClose) {
                    onClose();
                }
            } else {
                toast.error(response.message || "Failed to add attachments");
            }
        } catch (error) {
            console.error("Error adding attachments:", error);
            // Extract proper error message from API response
            let errorMessage = "Failed to add attachments";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    /**
     * Handle cancel
     */
    const handleCancel = () => {
        setFiles([]);
        if (onClose) {
            onClose();
        }
    };

    return (
        <div className="add-attachments-overlay" onClick={handleCancel}>
            <div className="add-attachments-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="add-attachments-header">
                    <h3>
                        <i className="las la-paperclip" /> Add Attachments
                    </h3>
                    <button onClick={handleCancel} className="close-button">
                        <i className="las la-times" />
                    </button>
                </div>

                <div className="add-attachments-content">
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{
                            display: 'inline-block',
                            padding: '12px 20px',
                            background: '#f8f9fa',
                            border: '2px dashed #d1d5db',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#6b7280',
                            transition: 'all 0.2s',
                            width: '100%',
                            textAlign: 'center',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#e9ecef';
                            e.currentTarget.style.borderColor = '#3bac75';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f8f9fa';
                            e.currentTarget.style.borderColor = '#d1d5db';
                        }}
                        >
                            <i className="las la-cloud-upload-alt" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }} />
                            Click to upload or drag and drop
                            <input
                                type="file"
                                multiple
                                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.mov,.avi,.mkv,.webm,.mp3,.wav,.m4v"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                        </label>
                        <small style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
                            Max 5 files, 50MB each. Supported: images, PDFs, documents, videos
                        </small>
                    </div>

                    {/* Display selected files */}
                    {files.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                                Selected Files ({files.length})
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {files.map((file, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '10px 14px',
                                            background: '#f3f4f6',
                                            borderRadius: '8px',
                                            border: '1px solid #e5e7eb'
                                        }}
                                    >
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}>
                                            <i className={getFileIcon(file.name)} style={{ color: '#3bac75' }} />
                                            {file.name} <span style={{ color: '#6b7280' }}>({(file.size / 1024).toFixed(2)} KB)</span>
                                        </span>
                                        <button
                                            onClick={() => removeFile(index)}
                                            style={{
                                                background: 'transparent',
                                                color: '#dc2626',
                                                border: '1px solid #dc2626',
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                            }}
                                        >
                                            <i className="las la-times" /> Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="add-attachments-footer">
                    <button
                        onClick={handleCancel}
                        disabled={uploading}
                        style={{
                            padding: '10px 20px',
                            background: 'white',
                            color: '#6c757d',
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={uploading || files.length === 0}
                        style={{
                            padding: '10px 20px',
                            background: uploading || files.length === 0 ? '#6c757d' : '#3bac75',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: uploading || files.length === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        {uploading ? (
                            <>
                                <i className="las la-spinner la-spin" /> Uploading...
                            </>
                        ) : (
                            <>
                                <i className="las la-upload" /> Upload Attachments
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddAttachments;

